'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { ExternalLink, Trash2, Edit2, Bookmark, Edit, Search, X, Download, Briefcase } from 'lucide-react';
import { NewsClipping } from '@/utils/supabase-queries';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { Newspaper } from 'lucide-react';
import { getProxiedImageUrl } from '@/utils/image-proxy';
import { Loading } from '@/components/ui/loading';
import { supabase } from '@/utils/supabase';
import { Button } from '@/components/ui/button';
import { getTagColors } from '@/components/features/ActivityGraph/utils/styleUtils';
import { getLanguageCode } from '@/utils/languageUtils';

// Helper function to format reach with thousands separator
const formatReach = (reach: number | null | undefined): string => {
  if (!reach || reach === 0) return '0';
  return reach.toLocaleString('en-US');
};

// Component to handle image loading with better error handling
const CardImage = ({ 
  imageUrl, 
  alt 
}: { 
  imageUrl: string | undefined, 
  alt: string 
}) => {
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [useDirectImg, setUseDirectImg] = useState(false);

  useEffect(() => {
    if (!imageUrl) {
      setImgSrc('/images/news-placeholder.jpg');
      setIsLoading(false);
      setUseDirectImg(false);
      return;
    }
    if (imageUrl.startsWith('/') || 
        imageUrl.startsWith('layout/') ||
        (!imageUrl.startsWith('http') &&
         !imageUrl.startsWith('data:') && !imageUrl.startsWith('blob:'))) {
      setImgSrc('/images/news-placeholder.jpg');
      setIsLoading(false);
      setUseDirectImg(false);
      return;
    }
    try {
      const testUrl = new URL(imageUrl);
      const problematicDomains = [
        'protathlima.cyprustimes.com',
        'shootandgoal.cyprustimes.com', 
        'eurokerdos.cyprustimes.com',
        'knews.kathimerini.com.cy'
      ];
      const isProblematicDomain = problematicDomains.some(domain => imageUrl.includes(domain));
      if (isProblematicDomain) {
        const proxyUrl = `/api/simple-proxy?url=${encodeURIComponent(imageUrl)}&t=${Date.now()}`;
        setImgSrc(proxyUrl);
        setUseDirectImg(true); 
        setIsLoading(true);
      } else {
        setImgSrc(imageUrl);
        setUseDirectImg(false);
        setIsLoading(true);
      }
    } catch (e) {
      setImgSrc('/images/news-placeholder.jpg');
      setIsLoading(false);
      setHasError(true);
      setUseDirectImg(false);
    }
  }, [imageUrl]);

  const handleImageError = () => {
    if (!imageUrl) return;
    setImgSrc('/images/news-placeholder.jpg');
    setIsLoading(false);
    setHasError(true);
    setUseDirectImg(false);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  return (
    <div className="relative w-full h-full bg-slate-100 overflow-hidden">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
          <Loading />
        </div>
      )}
      {imgSrc && useDirectImg ? (
        <img
          src={imgSrc}
          alt={alt}
          className={`w-full h-full object-cover object-center transition-opacity duration-300 ${
            isLoading ? 'opacity-0' : 'opacity-100'
          }`}
          onError={handleImageError}
          onLoad={handleImageLoad}
        />
      ) : imgSrc ? (
        <Image
          src={imgSrc}
          alt={alt}
          className={`w-full h-full object-cover object-center transition-opacity duration-300 ${
            isLoading ? 'opacity-0' : 'opacity-100'
          }`}
          fill
          sizes="(max-width: 768px) 100vw, 400px"
          onError={handleImageError}
          onLoad={handleImageLoad}
          unoptimized={true}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-slate-200">
          <Newspaper size={32} className="text-slate-400" />
        </div>
      )}
      {process.env.NODE_ENV === 'development' && hasError && (
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1">
          Error loading image
        </div>
      )}
    </div>
  );
};

// Component to handle brand logo loading with better error handling
export const BrandLogo = ({ 
  brandName 
}: { 
  brandName: string | undefined
}) => {
  const [logoSrc, setLogoSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  
  useEffect(() => {
    async function fetchBrandLogo() {
      if (!brandName) {
        setHasError(true);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setHasError(false);
      try {
        const { data: exactMatch, error: exactError } = await supabase
          .from('Brands')
          .select('logo_url')
          .eq('Name', brandName)
          .maybeSingle();
        if (exactError) console.error(`BrandLogo: Error (exact): ${exactError.message}`);
        else if (exactMatch?.logo_url) {
          setLogoSrc(exactMatch.logo_url);
          setIsLoading(false);
          return;
        }
        const { data: caseInsensitive, error: caseError } = await supabase
          .from('Brands')
          .select('logo_url')
          .ilike('Name', brandName)
          .maybeSingle();
        if (caseError) console.error(`BrandLogo: Error (case-ins): ${caseError.message}`);
        else if (caseInsensitive?.logo_url) {
          setLogoSrc(caseInsensitive.logo_url);
          setIsLoading(false);
          return;
        }
        const { data: partialMatch, error: partialError } = await supabase
          .from('Brands')
          .select('logo_url')
          .ilike('Name', `%${brandName}%`)
          .maybeSingle();
        if (partialError) console.error(`BrandLogo: Error (partial): ${partialError.message}`);
        else if (partialMatch?.logo_url) {
          setLogoSrc(partialMatch.logo_url);
        } else {
          setHasError(true);
        }
      } catch (e: any) {
        console.error(`BrandLogo: Exception:`, e.message);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    }
    fetchBrandLogo();
  }, [brandName]);
  
  if (isLoading || hasError || !logoSrc) {
    return (
      <svg 
        className="w-3.5 h-3.5 mr-1.5 text-gray-400" 
        viewBox="0 0 24 24" 
        fill="currentColor"
        aria-label={isLoading ? "Loading brand logo" : `Placeholder for ${brandName} logo`}
      >
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
      </svg>
    );
  }
  
  return (
     <img 
      src={logoSrc}
      alt={`${brandName || 'Brand'} logo`} 
      className="w-3.5 h-3.5 mr-1.5 object-contain"
      onError={() => setHasError(true)} 
    />
  );
};

// Component to handle media outlet logo loading
export const MediaOutletLogo = ({ 
  outletName,
  logoUrl // Add new optional prop
}: { 
  outletName: string | undefined;
  logoUrl?: string | null; // Optional direct logo URL
}) => {
  const [logoSrc, setLogoSrc] = useState<string | null>(logoUrl || null); // Initialize with passed logoUrl
  const [isLoading, setIsLoading] = useState(!logoUrl && !!outletName); // Only load if no logoUrl and outletName exists
  const [hasError, setHasError] = useState(!logoUrl && !outletName); // Error if no logoUrl and no outletName

  useEffect(() => {
    // If a logoUrl is provided, or no outletName, don't fetch
    if (logoUrl || !outletName) {
      if (logoUrl) {
        setLogoSrc(logoUrl);
        setIsLoading(false);
        setHasError(false);
      } else {
        // No logoUrl and no outletName means we can't fetch
        setIsLoading(false);
        setHasError(true);
      }
      return;
    }

    // Reset state for fetching if outletName changes and no logoUrl was initially provided
    setLogoSrc(null);
    setIsLoading(true);
    setHasError(false);

    async function fetchOutletLogo() {
      if (!outletName) { // Should be caught by initial checks, but good to have
        setHasError(true);
        setIsLoading(false);
        return;
      }
      // setIsLoading(true); // Already set
      // setHasError(false); // Already set
      try {
        const { data: exactMatch, error: exactError } = await supabase
          .from('media_outlets')
          .select('logo_url')
          .eq('name', outletName)
          .maybeSingle();
        if (exactError) console.error(`MediaOutletLogo: Error (exact): ${exactError.message}`);
        else if (exactMatch?.logo_url) {
          setLogoSrc(exactMatch.logo_url);
          setIsLoading(false);
          return;
        }
        const { data: caseInsensitive, error: caseError } = await supabase
          .from('media_outlets')
          .select('logo_url')
          .ilike('name', outletName)
          .maybeSingle();
        if (caseError) console.error(`MediaOutletLogo: Error (case-ins): ${caseError.message}`);
        else if (caseInsensitive?.logo_url) {
          setLogoSrc(caseInsensitive.logo_url);
          setIsLoading(false);
          return;
        }
        const { data: partialMatch, error: partialError } = await supabase
          .from('media_outlets')
          .select('logo_url')
          .ilike('name', `%${outletName}%`)
          .maybeSingle();
        if (partialError) console.error(`MediaOutletLogo: Error (partial): ${partialError.message}`);
        else if (partialMatch?.logo_url) {
          setLogoSrc(partialMatch.logo_url);
        } else {
          setHasError(true);
        }
      } catch (e: any) { // Added type for e
        console.error(`MediaOutletLogo: Exception:`, e.message);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    }
    fetchOutletLogo();
  }, [outletName, logoUrl]); // Add logoUrl to dependencies

  if (isLoading) { // Simplified condition: only show loader when actively loading
    return (
      <svg 
        className="w-3.5 h-3.5 mr-1.5 text-gray-400 animate-pulse" // Added animate-pulse for loading
        viewBox="0 0 24 24" 
        fill="currentColor"
        aria-label="Loading outlet logo"
      >
        <path d="M19 3H5C3.9 3 3 3.9 3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"></path>
      </svg>
    );
  }
  
  if (hasError || !logoSrc) { // If error or no logoSrc after trying/not trying to load
    return (
      <svg 
        className="w-3.5 h-3.5 mr-1.5 text-gray-400" 
        viewBox="0 0 24 24" 
        fill="currentColor"
        aria-label={`Placeholder for ${outletName || 'outlet'} logo`}
      >
        <path d="M19 3H5C3.9 3 3 3.9 3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"></path>
      </svg>
    );
  }
  
  return (
     <img 
      src={logoSrc}
      alt={`${outletName || 'Media Outlet'} logo`} 
      className="w-3.5 h-3.5 mr-1.5 object-contain"
      onError={() => {
        // If the passed logoUrl fails, don't try to fetch, just show error.
        // If we fetched it and it failed, this will also be caught.
        setHasError(true); 
        setLogoSrc(null); // Clear logoSrc to trigger placeholder
      }} 
    />
  );
};

export interface ClipCardViewProps {
  filteredClips: NewsClipping[];
  selectedClips: string[];
  onSelectClip: (id: string, checked: boolean) => void;
  isLoading: boolean;
  totalClips: number;
  onDeleteClip?: (id: string) => void; // Kept for potential future use
  onEditClip?: (id: string) => void; // Kept for potential future use
  limit?: number;
  horizontalScroll?: boolean;
  hideCheckboxes?: boolean;
  savedClipIds?: Set<string>;
  onToggleSave?: (id: string) => Promise<void>;
  onDeleteClips?: (ids: string[]) => void; // Kept for potential future use
  cardSpacing?: string;
  entityType?: 'media-clips' | 'media-outlets' | 'brands';
  canUpdate?: boolean; // Kept for potential future use
  canDelete?: boolean; // Kept for potential future use
  onClipClick?: (clipId: string) => void; // *** ADDED: Handler for clicking the card ***
}

export const ClipCardView = ({
  filteredClips,
  selectedClips,
  onSelectClip,
  isLoading,
  totalClips,
  // onDeleteClip, // Commented out as not used in this card view directly
  // onEditClip, // Commented out as not used in this card view directly
  limit,
  horizontalScroll = false,
  hideCheckboxes = false,
  savedClipIds = new Set<string>(),
  onToggleSave,
  // onDeleteClips, // Commented out as not used in this card view directly
  cardSpacing = 'space-x-3',
  entityType = 'media-clips',
  // canUpdate, // Commented out as not used in this card view directly
  // canDelete, // Commented out as not used in this card view directly
  onClipClick // *** ADDED: Destructure the handler ***
}: ClipCardViewProps) => {
  
  // Prevent card click when clicking interactive elements
  const stopPropagation = (e: React.MouseEvent) => {
      e.stopPropagation();
  };

  // Format date to DD/MM/YY
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString().slice(-2); // Get last 2 digits
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString; // Return original on error
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center" role="status" aria-label="Loading media clips">
        <Loading size="lg" text="Loading media clips..." />
      </div>
    );
  }

  if (filteredClips.length === 0) {
    return (
      <div className="flex h-64 flex-col items-center justify-center p-6 text-center" role="status" aria-label="No media clips found">
        <div className="rounded-full bg-gray-100 p-3">
          <Newspaper className="h-8 w-8 text-gray-400" aria-hidden="true" />
        </div>
        <h3 className="mt-3 text-sm font-medium text-gray-900">No clippings found</h3>
        <p className="mt-1 text-sm text-gray-500">
          {totalClips > 0
            ? "Try changing your search or filter criteria."
            : "There are no media clippings in the database yet."}
        </p>
      </div>
    );
  }

  // Use div for horizontal scrolling if enabled
  const Container = horizontalScroll ? 'div' : 'ul';
  const containerProps = horizontalScroll
    ? { className: `flex overflow-x-auto py-2 ${cardSpacing}` }
    : { className: "grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5" };
    
  const clipsToShow = limit ? filteredClips.slice(0, limit) : filteredClips;

  return (
    <Container {...containerProps}>
      {clipsToShow.map((clip) => {
        const isSelected = selectedClips.includes(String(clip.id));
        const isSaved = savedClipIds.has(String(clip.id));
        
        return (
          // *** ADDED: onClick handler and cursor-pointer ***
          <li 
            key={clip.id} 
            className={`relative flex flex-col rounded-lg bg-white shadow-md border border-gray-100 overflow-hidden transition-all duration-200 ease-in-out hover:shadow-lg ${horizontalScroll ? 'w-64 flex-shrink-0' : ''} ${isSelected ? 'ring-2 ring-offset-2 ring-[#8A6FE8]' : ''} cursor-pointer`}
            onClick={() => onClipClick?.(String(clip.id))} 
            role="button" // Indicate it's clickable
            tabIndex={0} // Make it focusable
            aria-label={`View details for ${clip.title}`}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClipClick?.(String(clip.id)); }} // Keyboard accessibility
          >
            {/* Card Header - Image */}
            <div className="h-32 sm:h-40 w-full flex-shrink-0">
              <CardImage imageUrl={clip.image_url || undefined} alt={clip.title || 'Media Clip'} />
            </div>
            
            {/* Checkbox - Top Left */}
            {!hideCheckboxes && (
              // *** ADDED: stopPropagation ***
              <label htmlFor={`card-checkbox-${clip.id}`} className="absolute top-2 left-2 z-10" onClick={stopPropagation}>
                <input
                  type="checkbox"
                  id={`card-checkbox-${clip.id}`}
                  checked={isSelected}
                  onChange={(e) => onSelectClip(String(clip.id), e.target.checked)}
                  className="sr-only peer"
                  aria-label={`Select ${clip.title}`}
                />
                <div className="h-5 w-5 rounded border border-gray-400 bg-white/70 shadow-sm peer-checked:bg-[#8A6FE8] peer-checked:border-[#8A6FE8] flex items-center justify-center cursor-pointer backdrop-blur-sm">
                  {isSelected && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                  )}
                </div>
              </label>
            )}

            {/* Save Button - Top Right */}
            {onToggleSave && (
               // *** ADDED: stopPropagation ***
              <button 
                onClick={(e) => { stopPropagation(e); onToggleSave(String(clip.id)); }}
                className="absolute top-2 right-2 z-10 p-1 rounded-full bg-white/70 hover:bg-white text-gray-600 shadow-sm backdrop-blur-sm"
                title={isSaved ? "Remove from saved" : "Save clip"}
                aria-label={isSaved ? "Remove from saved" : "Save clip"}
              >
                <Bookmark className={`h-4 w-4 ${isSaved ? "text-[#8A6FE8] fill-[#8A6FE8]" : "text-gray-500"}`} />
              </button>
            )}

            {/* Card Body */}
            <div className="flex flex-1 flex-col justify-between p-3 pt-3 pb-2 sm:p-4 sm:pt-4 sm:pb-2 space-y-2">
              <div className="flex-1 space-y-1.5">
                {/* Title */}
                <h3 className="text-sm sm:text-base font-semibold text-gray-900 line-clamp-2">
                  {clip.title}
                </h3>
                
                {/* Source & Date */}
                <div className="text-xs text-gray-500">
                  {clip.source && (
                    <div>
                      <a 
                        href={clip.url || '#'} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center hover:text-[#8A6FE8] hover:underline"
                        onClick={(e) => { if (clip.url) stopPropagation(e); }}
                      >
                        {clip.source} {clip.url && <ExternalLink className="ml-1 h-3 w-3" />}
                      </a>
                    </div>
                  )}
                  {clip.date && (
                    <div className="mt-1">
                      <span className="whitespace-nowrap">{formatDate(clip.date)}</span>
                    </div>
                  )}
                </div>
                
                {/* Tags - Sentiment & Category */}
                <div className="flex flex-wrap items-center gap-1 pt-1">
                  {/* Sentiment Tag */}
                  {clip.sentiment && (() => {
                     const { backgroundColor, textColor } = getTagColors('sentiment', clip.sentiment!);
                     return (
                       <span 
                         className="inline-flex items-center rounded-full px-2 py-0.5 text-xxs font-medium"
                         style={{ backgroundColor, color: textColor }}
                       >
                         {clip.sentiment}
                       </span>
                     );
                  })()}
                  {clip.category && (() => {
                     const { backgroundColor, textColor } = getTagColors('category', clip.category!);
                     return (
                       <span 
                         className="inline-flex items-center rounded-full px-2 py-0.5 text-xxs font-medium"
                         style={{ backgroundColor, color: textColor }}
                       >
                         {clip.category}
                       </span>
                     );
                  })()}
                  {/* Language Tag - ADDED & MODIFIED */} 
                  {clip.Language && (() => {
                     const langCode = getLanguageCode(clip.Language);
                     return (
                       <span 
                         className="inline-flex items-center rounded-full px-2 py-0.5 text-xxs font-medium bg-gray-100 text-gray-700"
                         title={clip.Language} // Show full language name on hover
                       >
                         {langCode.toLowerCase()}
                       </span>
                     );
                  })()}
                </div>
              </div>

              {/* Brand Tag & Reach - Bottom Row */}
              {(clip.brandName || (clip.reach && clip.reach > 0)) && (
                <div className="pt-1 border-t border-gray-100 flex items-center justify-between"> 
                  {clip.brandName && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-gray-700 font-medium text-xs">
                      <BrandLogo brandName={clip.brandName} /> 
                      {clip.brandName}
                    </span>
                  )}
                  {/* Reach Tag - Aligned to the right */}
                  {clip.reach && clip.reach > 0 && (
                    <span 
                      className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xxs font-medium text-blue-800"
                      title={`Estimated reach: ${formatReach(clip.reach)}`}
                    >
                      Reach: {formatReach(clip.reach)}
                    </span>
                  )}
                </div>
              )}
              
              {/* Optional Summary Placeholder */}
              {/* 
              {clip.summary && (
                <p className="mt-2 text-xs text-gray-600 line-clamp-2">{clip.summary}</p>
              )}
              */}
            </div>
          </li>
        );
      })}
    </Container>
  );
}; 